import cookie from '@s-ui/js/lib/cookie'
import {getDecision} from '@s-ui/pde'

import {stripSlash} from '../../helpers/index.js'
import {
  addNamesToExcludedVehicles,
  getMakesWithModelsOptions,
  getPageUrlFAQsInfo,
  mergeFavoritesResults
} from './utils.js'

export const NOTIFICATIONS = {
  AD_NOT_AVAILABLE: 'AD_NOT_AVAILABLE',
  HAS_SAVED_FAVORITE_SUCCESS: 'HAS_SAVED_FAVORITE_SUCCESS',
  HAS_SAVED_FAVORITE_ERROR: 'HAS_SAVED_FAVORITE_ERROR'
}

export const PAGINATION_PLACEHOLDER = '%{pageNumber}'

const FAVORITES_USE_CASE_RESPONSE_FALLBACK = {ads: [], dealers: []}

const INITIAL_ADS_USE_CASE_RESPONSE_FALLBACK = {
  items: [],
  totalResults: 0,
  totalPages: 0
}

const PROMISE_REJECTED = 'rejected'

const ASPECT_RATIO = 1.33
const MOBILE_WIDTH = 560
const DEFAULT_PHOTO = 'images/no-photo.png'

const _getStructuredData = ({scripts}) => scripts.map(script => JSON.parse(script.innerHTML))

const getCroppedImageSrc = ({src, width}) => {
  const aspectRatioHeight = Math.floor(width / ASPECT_RATIO)
  const crop = `${width}x${aspectRatioHeight}cut`
  return `${src}/${crop}/`
}

export async function getInitialProps({routeInfo, context: {cookies, domain, i18n, pde}}) {
  const {culture} = i18n
  const {
    location: {
      pathname: path,
      query,
      search: queryParams,
      state: {
        lastFilter,
        origin,
        discardAd,
        searchAiNotificationType,
        reservationAdNotAvailable: reservationAdNotAvailableNotification
      } = {}
    }
  } = routeInfo

  const config = domain.get('config')
  const {decide} = getDecision(pde)

  const {PAGE_NAMES_BY_LISTING_SECTION_ID} = config.get('page')

  const {
    QUERY_PARAMS,
    RECOMMENDED_ADS,
    COOKIES: {
      IS_VIEWED_SORT_MANAGEMENT_NOTIFICATION,
      VISITED_AD: visitedAdCookieKey,
      AD_NOT_AVAILABLE: adNotAvailableCookieKey
    },
    LISTING_SECTION_IDS
  } = config.get('COMMON')

  const getCookiesData = () => {
    const isClientSide = typeof window !== 'undefined'
    if (isClientSide) {
      return {
        [adNotAvailableCookieKey]: cookie.get(adNotAvailableCookieKey),
        [visitedAdCookieKey]: cookie.get(visitedAdCookieKey),
        [IS_VIEWED_SORT_MANAGEMENT_NOTIFICATION]: cookie.get(IS_VIEWED_SORT_MANAGEMENT_NOTIFICATION)
      }
    } else {
      return cookie.parse(cookies)
    }
  }

  const cookiesData = getCookiesData()

  const {
    [adNotAvailableCookieKey]: AdNotAvailable,
    [visitedAdCookieKey]: visitedAdId,
    [IS_VIEWED_SORT_MANAGEMENT_NOTIFICATION]: isViewedSortManagementNotification
  } = cookiesData

  const hasComeFromAdNotAvailable = Boolean(AdNotAvailable)

  const recommendedAdsConfig = RECOMMENDED_ADS.adsList
  const {maxRecommendations, minRecommendations, minResultsToShowRecommendations} = recommendedAdsConfig

  const {
    ADS_SEARCH_TOPBAR_FILTERS_MAP,
    bodyTypes: bodyTypesConfig,
    CATEGORY2_MAP,
    colors: colorsConfig,
    CREATE_ALERT_QUERY_PARAM,
    DEFAULT_SEARCH_TOPBAR_FILTERS_MAP,
    DEFAULT_SEARCH,
    FILTER_NAMES,
    fuelTypes: fueltypesConfig,
    HAS_RECOMMENDER: isRecommenderActive,
    OFFER_TYPE_NEW,
    OFFER_TYPE_KM0,
    OFFER_TYPE_SECOND_HAND,
    specificFueltypes: specificFueltypesConfig
  } = config.get('search')

  const {PATHS} = config.get('COMMON')

  const isAdsListSearchPage = path === PATHS.ADS_LIST_SEARCH

  const provincesConfig = config.get('provinces')
  const defaultPaginationPlaceholderUrl = '#'

  const {REFERER} = config.get('REQUEST_HEADERS')
  const referer = config.get(REFERER)

  const hasNotAvailableNotification = Boolean(hasComeFromAdNotAvailable)

  const _getBodyTypeLiteral = id => {
    const bodyTypeLiteral = bodyTypesConfig.find(bodyTypeConfig => bodyTypeConfig.id === id)?.label
    return i18n.t(bodyTypeLiteral)
  }
  const _getProvinceLiteral = id => provincesConfig.find(provinceConfig => provinceConfig.id === id)?.label

  // eslint-disable-next-line react/prop-types
  const _getCategory2Literal = ({categoryId, category2Id}) => {
    const items = CATEGORY2_MAP[categoryId]
    const category2Literal = items?.find(category2 => category2.id === category2Id)?.label
    return category2Literal ? i18n.t(category2Literal) : null
  }

  const _getFuelTypeLiteral = id => {
    const config = specificFueltypesConfig || fueltypesConfig
    const fuelTypeLiteral = config.find(fuelTypeConfig => fuelTypeConfig.id === id)?.label
    return i18n.t(fuelTypeLiteral)
  }

  const _getColorLiteral = ({colorId}) => {
    const colorLiteral = colorsConfig.find(colorConfig => Number(colorConfig.id) === colorId)?.label
    return i18n.t(colorLiteral)
  }

  // eslint-disable-next-line react/prop-types
  const _getTransmissionLiteral = ({transmissionTypeId}) => {
    const {transmissionTypes} = config.get('search')
    if (!transmissionTypes || transmissionTypeId === 0) return null

    const transmission = transmissionTypes.find(transmission => transmission.id === transmissionTypeId)
    return transmission ? i18n.t(transmission.label) : null
  }

  // eslint-disable-next-line react/prop-types
  const _getEnvironmentalLabelLiteral = ({environmentalLabels}) => {
    // eslint-disable-next-line react/prop-types
    if (!environmentalLabels || !environmentalLabels.length) return null

    const {environmentalLabels: environmentalLabelConfig} = config.get('search')
    const [firstLabelSelected] = environmentalLabels

    const environmentalLabel = environmentalLabelConfig.find(label => label.id === firstLabelSelected)

    return environmentalLabel ? i18n.t(environmentalLabel.label) : null
  }

  // eslint-disable-next-line react/prop-types
  const _getEnvironmentalLabelValue = ({environmentalLabels}) => {
    // eslint-disable-next-line react/prop-types
    if (!environmentalLabels || !environmentalLabels.length) return null

    const [firstLabelSelected] = environmentalLabels
    return firstLabelSelected
  }

  const {
    enabled: isListinRelevanceIncommingExperimentActive,
    variationKey: listinRelevanceIncommingExperimentVariationKey
  } = decide('ff_web_pro_team_listing_relevance_incomming', {
    queryString: queryParams
  })

  const {enabled: isGetResultsFromSearchUseCaseActive} = decide(
    'ff_web_buyer_get_results_from_search_use_case_implementation'
  )

  const {enabled: isRemoteSeoContentEnabled} = decide('ff_web_growth_ai_seo_content')
  const {enabled: isAIFaqsForSearchEnabled} = decide('ff_web_growth_ai_faqs', {queryString: queryParams})

  const _getSearchData = () =>
    domain.get('get_search_data_from_url_search_use_case').execute({path, query, url: path + queryParams})

  const _getFaqsForUrlSearch = () => domain.get('get_faqs_for_url_search_use_case').execute({targetUrl: path})

  // `Promise.allSettled` yields `{status: 'rejected', reason}` with NO `value` when a promise
  // rejects, so destructuring `value` as an array throws "Invalid attempt to destructure
  // non-iterable instance" and takes the whole page down. The use cases normally resolve to
  // `[error, response]` tuples via `@AsyncInlineError()`, but the lazy `import()` behind
  // `domain.get(...)` can still reject on its own — a transient 502 on the
  // HttpSearchRepository chunk did exactly that on /segunda-mano/ in production.
  // The `= []` defaults let a rejection degrade to `undefined` values, which the code below
  // already tolerates (`searchDataResponse?.toFiltersSearch() || {}`).
  const [
    {value: [searchDataError, searchDataResponse] = []},
    {value: [faqsForUrlSearchError, faqsForUrlSearchResponse] = []}
  ] = await Promise.allSettled([_getSearchData(), _getFaqsForUrlSearch()])

  const {breadcrumbs, canonical, listingSectionId, metaData = {}, search} = searchDataResponse?.toFiltersSearch() || {}

  // The semantic URL resolver (get_search_data_from_url_search_use_case) never errors for a
  // garbage path — it falls back to LISTING_SECTION_IDS.ALL_VEHICLES, the id the domain config
  // names DEFAULT_LISTING_SECTION_ID, whenever it can't recognize the path as any real category.
  // Every real category (segunda-mano, km-0, renting, ...) gets its OWN id even for an unknown
  // nested slug (e.g. /segunda-mano/no-existe/ stays listingSectionId "2"), so this only fires
  // for paths the backend genuinely doesn't recognize at all. /search/ is the one legitimate page
  // that shares the default id (free-text search across every vehicle), hence the
  // isAdsListSearchPage exclusion. Verified against production for both cases before relying on
  // it — see the plan this came from.
  const isUnresolvedListingUrl = listingSectionId === LISTING_SECTION_IDS.ALL_VEHICLES && !isAdsListSearchPage
  if (isUnresolvedListingUrl) {
    throw new Response(null, {status: 404})
  }

  const pageName =
    PAGE_NAMES_BY_LISTING_SECTION_ID[listingSectionId] ||
    PAGE_NAMES_BY_LISTING_SECTION_ID[LISTING_SECTION_IDS.ALL_VEHICLES]

  const canonicalPathname = canonical && searchDataResponse?.getCanonicalPathname()
  const isAiFaqsForUrlSearchAllowed =
    isAIFaqsForSearchEnabled && search && stripSlash(path) === stripSlash(canonicalPathname)

  const {priceRange} = metaData

  const initialSearch = search || DEFAULT_SEARCH

  const {
    bodyTypeIds: rawBodyTypeIds = [],
    category2Id,
    categoryId,
    city,
    colorIds,
    entry,
    environmentalLabels: rawEnvironmentalLabels = [],
    filteredOfferTypeId,
    fuelTypeIds: rawFuelTypeIds = [],
    kms: {to: maxKms} = {},
    litres: {from: minLitres, to: maxLitres} = {},
    offerTypeId,
    price: {to: maxPrice} = {},
    provinceIds: rawProvinceIds = [],
    region,
    regionId,
    seats: {from: minSeats, to: maxSeats} = {},
    sellerTypeId,
    transmissionTypeId,
    vehicles = [],
    year: {from: minYear, to: maxYear} = {}
  } = initialSearch

  // Ensure arrays are always arrays, never false or other falsy values
  const bodyTypeIds = Array.isArray(rawBodyTypeIds) ? rawBodyTypeIds : []
  const environmentalLabels = Array.isArray(rawEnvironmentalLabels) ? rawEnvironmentalLabels : []
  const fuelTypeIds = Array.isArray(rawFuelTypeIds) ? rawFuelTypeIds : []
  const provinceIds = Array.isArray(rawProvinceIds) ? rawProvinceIds : []

  const [firstSelectedVehicle] = vehicles

  const hasSemanticMakeModel = Boolean(firstSelectedVehicle?.model?.id)

  const isNewVehicle = filteredOfferTypeId === OFFER_TYPE_NEW
  const isKm0Vehicle = offerTypeId === OFFER_TYPE_KM0
  const showNewVehicleFaq = hasSemanticMakeModel && (isNewVehicle || isKm0Vehicle)

  const _mapItems = ({items}) => {
    return items.map(item => {
      return {id: item.id, label: item.name}
    })
  }

  const _getAllMakes = () =>
    domain.get('get_info_vehicles_use_case').execute({
      nextField: FILTER_NAMES.MAKE,
      params: {categoryId}
    })

  /*
   * Resolve page information
   */
  const _maybeCreateAlert = () => {
    return query[CREATE_ALERT_QUERY_PARAM]
      ? domain.get('create_alert_user_alert_use_case').execute({search})
      : Promise.resolve()
  }

  const _getUserFavoriteAds = () => domain.get('get_user_favorites_use_case').execute()

  const _getCookieDiscardedAdsIds = () => {
    try {
      const config = domain.get('config')
      const cookie = config.get('cookie') || {}
      const COOKIE_PREFIX = config.get('cookieLayer').COOKIE_PREFIX_DISCARDED_ADS
      return Object.entries(cookie)
        .filter(([key]) => key.startsWith(COOKIE_PREFIX))
        .flatMap(([, value]) => {
          try {
            return JSON.parse(value)
          } catch {
            return []
          }
        })
    } catch {
      return []
    }
  }

  const [[, userDiscardedAds], cookieDiscardedIds] = await Promise.all([
    domain.get('get_user_discarded_ads_use_case').execute(),
    Promise.resolve(_getCookieDiscardedAdsIds())
  ])

  const httpDiscardedIds = userDiscardedAds?.items ?? []
  const allDiscardedIds = Array.from(new Set([...cookieDiscardedIds, ...httpDiscardedIds]))

  const _getVehiclesList = () => {
    return domain.get('get_search_results_ads_use_case').execute({
      ...initialSearch,
      excludedAdIds: allDiscardedIds.length ? allDiscardedIds : undefined,
      listingSectionId,
      withAggregations: true,
      variant: isListinRelevanceIncommingExperimentActive && listinRelevanceIncommingExperimentVariationKey
    })
  }

  const getResultsFromSearch = () =>
    domain.get('get_results_from_search_use_case').execute({listingSectionId, search: initialSearch})

  const _getPagination = () =>
    domain.get('get_search_pagination_url_ads_use_case').execute({
      listingSectionId,
      paginationPlaceholder: PAGINATION_PLACEHOLDER,
      queryParams: query,
      search: initialSearch,
      url: path
    })

  const _getTagClouds = () =>
    domain.get('get_tagcloud_seo_use_case').execute({
      bodyTypeId: bodyTypeIds[0],
      categoryId,
      filteredOfferTypeId,
      fuelTypeId: fuelTypeIds[0],
      hasAttributes: true,
      makeId: firstSelectedVehicle?.makeId,
      modelId: firstSelectedVehicle?.model?.id,
      offerTypeId,
      pageName,
      priceRangeGroup: maxPrice,
      provinceId: provinceIds[0],
      regionId,
      seats: minSeats || maxSeats,
      sellerTypeId,
      transmissionTypeId,
      version: firstSelectedVehicle?.version,
      year: minYear || maxYear
    })

  const _getWdylTagCloud = () =>
    domain.get('get_tagcloud_seo_use_case').execute({
      context: 'wdyl',
      makeId: firstSelectedVehicle?.makeId,
      modelId: firstSelectedVehicle?.model?.id,
      pageName
    })

  const getTrackingData = () => {
    const searchWithExcludedVehicleNames = {
      ...initialSearch,
      [FILTER_NAMES.EXCLUDED_VEHICLES]: addNamesToExcludedVehicles({
        excludedVehicles: initialSearch[FILTER_NAMES.EXCLUDED_VEHICLES],
        makesOptions: getMakesWithModelsOptions({makesWithModels: makesWithModels?.value})
      })
    }
    return domain.get('get_page_tracking_data_use_case').execute({
      advertisements: initialResultsResponse?.items,
      lastFilter,
      listingSectionId,
      ...(origin && {origin}),
      page: pageName,
      pageNumber: initialSearch?.page,
      referer,
      search: searchWithExcludedVehicleNames,
      searchResultsCount: initialResultsResponse?.totalResults
    })
  }

  const getMakesWithModels = () => domain.get('get_makes_with_models_vehicles_use_case').execute()

  const [
    {value: allMakes = {items: []}},
    {value: [initialResultsError, initialResultsResponse] = []},
    {value: [paginationError, paginationUrl] = []},
    {value: tagclouds = []},
    {value: extendedTagclouds = []},
    {status: maybeCreateAlertStatus, value: createdAlert},
    {value: userFavoriteAds},
    {value: [, makesWithModels] = []}
  ] = await Promise.allSettled([
    _getAllMakes(),
    isGetResultsFromSearchUseCaseActive ? getResultsFromSearch() : _getVehiclesList(),
    _getPagination(),
    _getTagClouds(),
    _getWdylTagCloud(),
    _maybeCreateAlert(),
    _getUserFavoriteAds(),
    getMakesWithModels()
  ])

  const makesOptions = _mapItems(allMakes || {items: []})
  const resultsForClient = initialResultsResponse || INITIAL_ADS_USE_CASE_RESPONSE_FALLBACK
  const favoriteAds = userFavoriteAds || FAVORITES_USE_CASE_RESPONSE_FALLBACK

  const initialResultsWithFavoritesData = resultsForClient.items.length
    ? {
        ...resultsForClient,
        items: mergeFavoritesResults({
          vehicles: resultsForClient.items,
          favorites: favoriteAds.ads
        }).map((item, index) => ({...item, list_position: index + 1}))
      }
    : resultsForClient

  const getVisitedAdData = () => {
    const position = resultsForClient.items.findIndex(({id}) => id === visitedAdId)
    const isAccessible = position >= 0
    return {id: visitedAdId, isAccessible, position}
  }

  const visitedAdData = getVisitedAdData()

  // Recommended ads for visited ad - showing recommendations below ad on list just after visiting
  const _getRecommendedAds = async () => {
    if (!visitedAdId || !isRecommenderActive) return {}

    const isFewResultsPage = resultsForClient.totalResults <= minResultsToShowRecommendations

    if (!isFewResultsPage && !visitedAdData.isAccessible) return {}

    const [recommendedAdsError, recommendedAdsResponse] = await domain.get('get_recommended_ads_use_case').execute({
      adId: visitedAdId,
      maxItems: maxRecommendations
    })

    if (recommendedAdsError) return

    if (recommendedAdsResponse.items.length >= minRecommendations) {
      return recommendedAdsResponse
    }
  }

  const _getSemanticMakeModeFromCanonical = () => {
    if (!canonical) return {}
    const MAKE_INDEX = 3
    const MODEL_INDEX = 4
    const splitUrl = canonical.split('/')
    return {
      urlMake: splitUrl[MAKE_INDEX],
      urlModel: splitUrl[MODEL_INDEX]
    }
  }

  const _getMakeModelFaqs = () => {
    const {urlMake, urlModel} = _getSemanticMakeModeFromCanonical()

    return domain.get('get_make_model_faqs_vehicles_use_case').execute({
      makeId: firstSelectedVehicle?.makeId,
      modelId: firstSelectedVehicle?.model?.id,
      make: urlMake,
      model: urlModel,
      pageName,
      path
    })
  }

  const _getAiFaqsForUrlSearch = () => {
    const hasResults = Boolean(initialResultsResponse?.items?.length)

    return isAiFaqsForUrlSearchAllowed && hasResults
      ? domain.get('get_ai_faqs_for_url_search_use_case').execute({listingSectionId, pageType: pageName, search, path})
      : Promise.resolve([])
  }

  const createAlertError = maybeCreateAlertStatus === PROMISE_REJECTED

  const paginationPlaceholderUrl = paginationError ? defaultPaginationPlaceholderUrl : paginationUrl

  const {VEHICLE_CONFIGURATOR_EMPTY_CASE_ENABLED, MAX_RESULTS_FOR_CONFIGURATOR_EMPTY_CASE} = config.get('configurator')

  const {totalResults} = initialResultsResponse || {}

  const showConfiguratorEmptyCase =
    VEHICLE_CONFIGURATOR_EMPTY_CASE_ENABLED &&
    listingSectionId === LISTING_SECTION_IDS.NEW_VEHICLES &&
    totalResults <= MAX_RESULTS_FOR_CONFIGURATOR_EMPTY_CASE &&
    Boolean(vehicles.length)

  const isNewVehicleOrKm0 = filteredOfferTypeId === OFFER_TYPE_NEW || filteredOfferTypeId === OFFER_TYPE_KM0
  const hasLessThan10Results = totalResults < 10

  const isConfiguratorAcceessEnabled =
    isNewVehicleOrKm0 &&
    hasLessThan10Results &&
    Boolean(firstSelectedVehicle?.make) &&
    Boolean(firstSelectedVehicle?.makeId)

  /*
   * Seo use cases
   */
  const _getSeoData = () => {
    const urlStatics = config.get('urlStatics')

    const hasResults = Boolean(initialResultsResponse?.totalResults) && Boolean(initialResultsResponse?.items?.length)
    const countTotalResults = hasResults && initialResultsResponse.totalResults
    const nResults = countTotalResults ? i18n.n(countTotalResults) : null
    const maxPriceLocal = maxPrice ? i18n.n(maxPrice) : null
    const [firstResult] = hasResults ? initialResultsResponse.items : []
    const width = MOBILE_WIDTH
    const firstResultHasImage = hasResults && Boolean(firstResult.img)
    const defaultPhotoSrc = `${urlStatics}${DEFAULT_PHOTO}`
    const firstImageSrc = firstResultHasImage ? getCroppedImageSrc({src: firstResult.img, width}) : defaultPhotoSrc
    const [firstSelectedColorId] = colorIds || []

    const seoUseCaseId = isRemoteSeoContentEnabled ? 'get_remote_seo_data_seo_use_case' : 'get_seo_data_use_case'

    return domain.get(seoUseCaseId).execute({
      culture,
      pageName,
      params: {
        bodyType: _getBodyTypeLiteral(bodyTypeIds?.[0]), // TODO: think about deprecating this type of param in order to centralize more the seo data calcs (MTR-56077)
        bodyTypeIds: Array.isArray(bodyTypeIds) ? bodyTypeIds : [],
        breadcrumb: breadcrumbs,
        canonical,
        category2: _getCategory2Literal({categoryId, category2Id}), // TODO: to be reviewed in MTR-56077
        categoryId,
        color: _getColorLiteral({colorId: firstSelectedColorId}),
        city,
        entry,
        environmentalLabel: _getEnvironmentalLabelLiteral({environmentalLabels}),
        environmentalLabelValue: _getEnvironmentalLabelValue({environmentalLabels}),
        fuelType: _getFuelTypeLiteral(fuelTypeIds[0]), // TODO: to be reviewed in MTR-56077m
        isCrawlableAndIndexable: showConfiguratorEmptyCase,
        listingSectionId,
        make: firstSelectedVehicle?.make,
        maxKms: maxKms || null,
        maxPrice: {
          value: maxPrice,
          text: maxPriceLocal
        },
        model: firstSelectedVehicle?.model?.name,
        nResults,
        offerTypeId,
        filteredOfferTypeId,
        page: query.pg,
        path,
        province: _getProvinceLiteral(provinceIds?.[0]), // TODO: to be reviewed in MTR-56077
        region,
        seats: maxSeats,
        sellerTypeId,
        transmission: _getTransmissionLiteral({transmissionTypeId}),
        version: firstSelectedVehicle?.version,
        year: minYear || maxYear,
        ...priceRange,
        litres: minLitres || maxLitres
      },
      preloadImages: [firstImageSrc],
      vehicleCount: initialResultsResponse?.totalResults
    })
  }

  const _getStatsData = () => {
    const {page} = initialSearch || {}

    const {totalResults} = initialResultsResponse || {}

    const {IS_STATS_DATA_ENABLED: isStatsDataEnabled} = config.get('search')

    const isFirstPage = page === 1
    const hasMoreThanOneResults = totalResults > 1
    const isSecondHandOfferType = offerTypeId === OFFER_TYPE_SECOND_HAND
    const hasOnlyOneVehicle = vehicles.length === 1
    const hasMake = Boolean(firstSelectedVehicle?.make)

    const needStatsData =
      isStatsDataEnabled &&
      hasMake &&
      isFirstPage &&
      hasMoreThanOneResults &&
      isSecondHandOfferType &&
      hasOnlyOneVehicle

    return needStatsData
      ? domain.get('get_search_stats_ads_use_case').execute({
          ...initialSearch
        })
      : Promise.resolve()
  }

  const _getConfiguratorEmptyCaseData = async () => {
    const {VEHICLE_CONFIGURATOR_PAGE_NAME} = config.get('page')

    return showConfiguratorEmptyCase
      ? domain.get('get_url_page_use_case').execute({
          pageName: VEHICLE_CONFIGURATOR_PAGE_NAME,
          params: {
            makeId: firstSelectedVehicle?.makeId,
            modelId: firstSelectedVehicle?.model?.id
          }
        })
      : Promise.resolve([])
  }

  const _getIsMakeHiredDealer = async () => {
    return domain.get('get_is_make_hired_dealer_use_case').execute({
      makeId: firstSelectedVehicle.makeId
    })
  }

  const _getConfiguratorData = async () => {
    return domain.get('get_search_models_vehicles_use_case').execute({
      makeId: firstSelectedVehicle.makeId,
      modelId: firstSelectedVehicle.model?.id
    })
  }

  const [
    [seoDataError, seoDataResponse],
    [statsError, {stats: statsResponse} = {}] = [],
    trackingData,
    [makeModelFaqsError, makeModelFaqs],
    recommendedAds,
    [configuratorEmptyCaseError, configuratorEmptyCaseData],
    [isMakeHiredDealerError, isMakeHiredDealerResponse] = [null, null],
    [configuratorDataError, configuratorDataResponse] = [null, null],
    [aiFaqsError, aiFaqsResponse]
  ] = await Promise.all([
    _getSeoData(),
    _getStatsData(),
    getTrackingData(),
    _getMakeModelFaqs(),
    _getRecommendedAds(),
    _getConfiguratorEmptyCaseData(),
    isConfiguratorAcceessEnabled ? _getIsMakeHiredDealer() : Promise.resolve([null, null]),
    isConfiguratorAcceessEnabled ? _getConfiguratorData() : Promise.resolve([null, null]),
    _getAiFaqsForUrlSearch()
  ])

  const [faqsForUrlError, faqsForUrlResponse] = aiFaqsResponse
    ? [aiFaqsError, aiFaqsResponse]
    : [faqsForUrlSearchError, faqsForUrlSearchResponse]

  const {faqsForUrlSectionTitle, faqsForUrlItems} = getPageUrlFAQsInfo({
    i18n,
    faqsForUrlError,
    faqsForUrlResponse,
    path
  })

  const {head: helmet = {}, h1, staticFallbackH1} = seoDataResponse || {}

  const isH1FromRemoteSeoContent = staticFallbackH1 !== undefined

  const dynamicH1 = isH1FromRemoteSeoContent ? h1 : undefined
  const staticH1 = isH1FromRemoteSeoContent ? staticFallbackH1 : h1
  const {scripts: structuredDataScripts = []} = helmet

  const structuredDataJsonList = _getStructuredData({
    scripts: structuredDataScripts
  })

  // ads-list pages perform two requests to tagclouds endpoint
  const mergedTagclouds = [...tagclouds, ...extendedTagclouds]

  const getFiltersList = () => {
    const isAllVehicles = listingSectionId === LISTING_SECTION_IDS.ALL_VEHICLES
    const categoryFilters = ADS_SEARCH_TOPBAR_FILTERS_MAP[categoryId] || {}
    return isAllVehicles
      ? DEFAULT_SEARCH_TOPBAR_FILTERS_MAP
      : categoryFilters[offerTypeId] || DEFAULT_SEARCH_TOPBAR_FILTERS_MAP
  }

  const filtersList = getFiltersList()

  const [configuratorEmptyCaseUrl] = configuratorEmptyCaseData?.getUrl() || []

  const isMakeHiredDealer = Boolean(isMakeHiredDealerResponse?.isHired) && !isMakeHiredDealerError

  let configuratorData = null
  if (!configuratorDataError && configuratorDataResponse) {
    const firstModel = configuratorDataResponse.getFirstModel()
    configuratorData = firstModel
  }

  return {
    breadcrumb: breadcrumbs,
    configuratorData,
    configuratorEmptyCaseUrl,
    createdAlert,
    discardAd,
    faqsForUrlItems,
    faqsForUrlSectionTitle,
    dynamicH1,
    helmet,
    isMakeHiredDealer,
    staticH1,
    structuredDataJsonList,
    initialSearch: {
      // to remove with https://jira.ets.mpi-internal.com/browse/MTR-81755
      ...initialSearch,
      [FILTER_NAMES.EXCLUDED_VEHICLES]: addNamesToExcludedVehicles({
        excludedVehicles: initialSearch[FILTER_NAMES.EXCLUDED_VEHICLES],
        makesOptions: getMakesWithModelsOptions({makesWithModels: makesWithModels?.value})
      }),
      make: firstSelectedVehicle?.make,
      makeId: firstSelectedVehicle?.makeId,
      model: {id: firstSelectedVehicle?.model?.id, name: firstSelectedVehicle?.model?.name ?? ''},
      version: firstSelectedVehicle?.version
    },
    lastFilter,
    listFiltersOptions: {
      [FILTER_NAMES.MAKE]: {options: makesOptions},
      [FILTER_NAMES.VEHICLES]: {
        allowExcludeFilter: true,
        options: getMakesWithModelsOptions({makesWithModels: makesWithModels?.value})
      }
    },
    makeModelFaqs,
    showCardConfigurator: Boolean(configuratorData) && isConfiguratorAcceessEnabled,
    showNewVehicleFaq,
    initialResults: initialResultsWithFavoritesData,
    isAdsListSearchPage,
    listingSectionId,
    pageName,
    pageErrors: {
      configuratorEmptyCaseError,
      createAlertError,
      faqsForUrlError,
      initialResultsError,
      makeModelFaqsError,
      searchDataError,
      seoDataError,
      statsError
    },
    paginationPlaceholderUrl,
    notifications: {
      [NOTIFICATIONS.AD_NOT_AVAILABLE]: hasNotAvailableNotification
    },
    hasComeFromAdNotAvailable,
    reservationAdNotAvailableNotification,
    searchFaqs: statsResponse,
    searchAiNotificationType,
    showSortManagementNotification: !isViewedSortManagementNotification,
    tagclouds: mergedTagclouds,
    trackingData,
    filtersList,
    visitedAdData,
    isRecommendedAdsFallback: recommendedAds?.isRecommendedAdsFallback || false,
    recommendedAds,
    hasNoMatchesUserAlertInformation: Boolean(query[QUERY_PARAMS.ALERTS.NO_MATCHES])
  }
}
